import * as React from "react";
import { CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

export type MonthYear = { year: number; month: number }; // month is 1-12

function toIndex(v: MonthYear): number {
  return v.year * 12 + (v.month - 1);
}

function fromIndex(i: number): MonthYear {
  return { year: Math.floor(i / 12), month: (i % 12) + 1 };
}

function label(v: MonthYear): string {
  return `${MONTH_LABELS[v.month - 1].slice(0, 3)} ${v.year}`;
}

// A month-granularity range picker, following the same Popover-wrapping-
// own-controls construction as DateTimePicker.tsx (own component tree
// instead of a third-party range-picker library) - two Select pairs
// (year, month) instead of a day-level Calendar, since the picker's
// granularity is months, not days.
export function MonthRangePicker({
  start,
  end,
  onChange,
  disabled
}: {
  start: MonthYear;
  end: MonthYear;
  onChange: (start: MonthYear, end: MonthYear) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - 4 + i); // last 4 years .. next 1 year

  function setStart(next: MonthYear) {
    // Clamp end forward if this edit would invert the range, rather than
    // silently accepting start > end (mirrors DateTimePicker's self-
    // contained onChange handlers - validation stays inside this component).
    onChange(next, toIndex(next) > toIndex(end) ? next : end);
  }

  function setEnd(next: MonthYear) {
    onChange(toIndex(next) < toIndex(start) ? next : start, next);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn("justify-start font-normal")}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {label(start)} – {label(end)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="start">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="text-xs font-medium text-muted-foreground">
              From
            </div>
            <Select
              value={String(start.month)}
              onValueChange={(m) => setStart({ ...start, month: Number(m) })}
            >
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTH_LABELS.map((m, i) => (
                  <SelectItem key={m} value={String(i + 1)}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={String(start.year)}
              onValueChange={(y) => setStart({ ...start, year: Number(y) })}
            >
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <div className="text-xs font-medium text-muted-foreground">To</div>
            <Select
              value={String(end.month)}
              onValueChange={(m) => setEnd({ ...end, month: Number(m) })}
            >
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTH_LABELS.map((m, i) => (
                  <SelectItem key={m} value={String(i + 1)}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={String(end.year)}
              onValueChange={(y) => setEnd({ ...end, year: Number(y) })}
            >
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Every month from `start` to `end`, inclusive, as MonthYear values. */
export function monthsInRange(start: MonthYear, end: MonthYear): MonthYear[] {
  const out: MonthYear[] = [];
  for (let i = toIndex(start); i <= toIndex(end); i++) out.push(fromIndex(i));
  return out;
}

export function monthKey(v: MonthYear): string {
  return `${v.year}-${String(v.month).padStart(2, "0")}`;
}

export function monthLabel(v: MonthYear): string {
  return label(v);
}

export function defaultTrailingRange(monthsBack: number): {
  start: MonthYear;
  end: MonthYear;
} {
  const now = new Date();
  const end: MonthYear = { year: now.getFullYear(), month: now.getMonth() + 1 };
  const start = fromIndex(toIndex(end) - (monthsBack - 1));
  return { start, end };
}
